import { Injectable, InjectionToken, inject } from '@angular/core';
import { Analytics, logEvent } from '@angular/fire/analytics';

type Flag = boolean;
export type StudentLearningMode = 'home_learning' | 'homework' | 'exam' | 'classwork';
export type StudentConfidenceLevel = 'GUESSING' | 'LESS_CONFIDENT' | 'MORE_CONFIDENT' | 'MOST_CONFIDENT';
export type StudentKnowledgeLevel = 'mastered' | 'good' | 'shaky' | 'weak' | 'unseen';

/** PRD 13.1 student event table: the only events and (non-personal) parameters that may be logged. */
export interface StudentAnalyticsEvents {
  student_app_started: { platform: 'web' | 'android' | 'ios' };
  student_splash_played: { reduced_motion: Flag };
  student_learning_started: { mode: StudentLearningMode };
  student_question_answered: { mode: StudentLearningMode; is_correct: Flag; confidence_level: StudentConfidenceLevel | string };
  student_block_completed: { mode: StudentLearningMode; correct_count: number };
  student_block_flawless: { mode: StudentLearningMode };
  student_review_opened: { mode: StudentLearningMode; wrong_count: number };
  student_review_row_opened: { is_correct: Flag };
  student_ke_content_opened: { knowledge_level: StudentKnowledgeLevel };
  student_block_end_shown: { correct_count: number; is_milestone: Flag };
  student_homework_end_shown: { correct_count: number; total_count: number };
  student_milestone_reached: { threshold: number; subject_id: string };
  student_animation_failed: { animation: string };
  student_sound_failed: { sound: 'unlock' | 'elephant' | 'trumpet'; error_code: string };
}

/** Indirection over Firebase's logEvent so tests can observe the calls. */
export const ANALYTICS_LOG_EVENT = new InjectionToken<(analytics: Analytics, name: string, params: Record<string, unknown>) => void>('ANALYTICS_LOG_EVENT', { providedIn: 'root', factory: () => logEvent as (analytics: Analytics, name: string, params: Record<string, unknown>) => void });

const FORBIDDEN_PREFIXES = ['firebase_', 'google_', 'ga_'];
const FORBIDDEN_PARAM_KEYS = /(^|_)(name|email|mail|text|query|search|title|message|password|phone|uid|user_id)$/;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly send = inject(ANALYTICS_LOG_EVENT);

  /** Reduces an error to a short code token; free text (which could hold personal data) becomes 'unknown'. */
  static errorCode(error: unknown): string {
    const source = error as { code?: unknown; name?: unknown; message?: unknown } | null;
    const raw = typeof error === 'string' ? error : ((typeof source?.code === 'string' ? source.code : undefined) ?? (source?.name !== 'Error' ? source?.name : undefined) ?? source?.message);
    return typeof raw === 'string' && /^[A-Za-z0-9_./-]{1,60}$/.test(raw) ? raw : 'unknown';
  }

  /** Fire-and-forget: never throws and never writes to Firestore. */
  log<K extends keyof StudentAnalyticsEvents>(name: K, params: StudentAnalyticsEvents[K]): void {
    try {
      if (!this.analytics || name.length > 40 || FORBIDDEN_PREFIXES.some(prefix => name.startsWith(prefix))) return;
      const safe: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
        if (!FORBIDDEN_PARAM_KEYS.test(key)) safe[key] = value;
      }
      this.send(this.analytics, name, safe);
    } catch {
      // analytics must never block navigation, saving or learning
    }
  }
}
